import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DropdownItem, DropdownProps, Extracted, NormalizedItem } from "./types";

type DropDownState<T> = Pick<
	DropdownProps<T>,
	| "items"
	| "selectedItem"
	| "selectedItems"
	| "onChange"
	| "onChangeMultiple"
	| "valueToString"
	| "multiple"
	| "minimalSelection"
	| "maximalSelection"
	| "multiAsOne"
	| "commit"
> & {
	/** Whether the panel is open (used to resync the apply-mode buffer when props change mid-open). */
	open?: boolean;
	/** Called when a toggle is refused for exceeding `maximalSelection` — lets the caller surface the cap. */
	onMaxExceeded?: () => void;
};

const stringifyLabel = (label: DropdownItem<unknown>["label"] | string, fallback: string): { node: NormalizedItem<unknown>["label"]; text: string } => {
	if (typeof label === "function") return { node: label(), text: fallback };
	if (typeof label === "string") return { node: label, text: label };
	return { node: fallback, text: fallback };
};

const normalizeItem = <T>(item: T | DropdownItem<Extracted<T>>, valueToString?: (value: Extracted<T>) => string): NormalizedItem<T> => {
	if (typeof item === "object" && item !== null && "value" in item) {
		const converted = item as DropdownItem<Extracted<T>>;
		// Canonical selection key: always derived from the value (valueToString ?? String) so it matches the
		// caller's `selectedItem(s)`. A custom `converted.id` is not used for matching (the legacy component
		// matched by valueToString too) — it would desync the selected set from the rendered rows.
		const id = valueToString?.(converted.value) ?? String(converted.value);
		// React key is the caller's own `id` when given (unique even if two items share a `value`),
		// falling back to the selection key. Selection stays keyed by `id` (value) — only the key differs.
		const key = converted.id !== undefined ? String(converted.id) : id;
		// A function label has no inherent text; prefer the caller's `searchText`, then the id. A string label
		// ignores the fallback (it carries its own text), so passing searchText here is harmless for that case.
		const { node: label, text: labelText } = stringifyLabel(converted.label, converted.searchText ?? id);
		const { node: selectedLabel, text: selectedText } = stringifyLabel(converted.selectedLabel ?? converted.label, labelText);
		return {
			value: converted.value,
			label,
			labelText,
			selectedLabel,
			selectedText,
			// Include the group name so typing it surfaces the whole group — otherwise the group header matches
			// but its children don't, and the panel drops a header with zero visible children.
			searchLabel: [labelText, selectedText, converted.secondaryLabel, converted.group].filter(Boolean).join(" "),
			id,
			key,
			icon: converted.icon,
			startAdornment: converted.startAdornment,
			color: converted.color,
			groupValues: converted.groupValues,
			disabled: converted.disabled,
			secondaryLabel: converted.secondaryLabel,
			endAdornment: converted.endAdornment,
			avatar: converted.avatar,
			group: converted.group,
		};
	}
	const value = item as Extracted<T>;
	const id = valueToString?.(value) ?? String(value);
	return { value, label: id, labelText: id, selectedLabel: id, selectedText: id, searchLabel: id, id, key: id };
};

/**
 * Owns the dropdown's selection as an **id-keyed set** that is independent of what the
 * panel renders/filters — this is what makes "search collapses the selection" impossible.
 * Selection is derived from the caller's `selectedItem(s)` in `live` mode, or from an internal
 * buffer in `apply` mode; every action toggles ids, then maps back to values for the callbacks.
 */
const useDropdownState = <T>({
	items,
	selectedItem,
	selectedItems,
	onChange,
	onChangeMultiple,
	valueToString,
	multiple,
	minimalSelection,
	maximalSelection,
	multiAsOne,
	commit = "live",
	open = false,
	onMaxExceeded,
}: DropDownState<T>) => {
	const idOf = useCallback((value: Extracted<T>) => valueToString?.(value) ?? String(value), [valueToString]);

	const normalizedItems = useMemo(() => items.map((item) => normalizeItem(item, valueToString)), [items, valueToString]);

	// Rows that participate in selection (group *headers* carry `groupValues` and are not themselves toggled).
	const selectableItems = useMemo(() => normalizedItems.filter((item) => !item.groupValues), [normalizedItems]);
	const selectableIds = useMemo(() => selectableItems.map((item) => item.id), [selectableItems]);

	// Selection is keyed by `id` (= `valueToString(value)`). If two selectable rows resolve to the same id, they
	// share one selection slot — toggling one toggles both, and the id maps back only to the last value. Detect
	// the collision here (pure) and surface the first offending key via analytics in the parent (a unique `id`
	// prop alone won't fix it — `id` is only the React key). See PR APP-477 review (duplicate nurse names).
	const { valueById, duplicateSelectionKey } = useMemo(() => {
		const map = new Map<string, Extracted<T>>();
		let duplicate: string | null = null;
		for (const item of normalizedItems) {
			if (!item.groupValues && duplicate === null && map.has(item.id)) duplicate = item.id;
			map.set(item.id, item.value);
		}
		return { valueById: map, duplicateSelectionKey: duplicate };
	}, [normalizedItems]);

	// Caller's committed selection as ids (source of truth in `live` mode).
	const committedIds = useMemo(() => {
		// Treat `null` like `undefined` (no selection): a single-select caller commonly passes `null` for
		// "nothing chosen" (e.g. `selectedItem={list[0] ?? null}`), which must NOT become a phantom "" id.
		const values = multiple ? (selectedItems ?? []) : selectedItem != null ? [selectedItem] : [];
		return values.map((v) => idOf(v));
	}, [multiple, selectedItems, selectedItem, idOf]);

	// `apply` mode buffers the selection until the user confirms. Reset from props whenever the panel opens.
	const [bufferIds, setBufferIds] = useState<string[]>(committedIds);
	// Most recent selection that satisfied `minimalSelection` — restored on close if the user leaves below min.
	const lastValidRef = useRef<string[]>(committedIds);
	const resetBuffer = useCallback(() => setBufferIds(committedIds), [committedIds]);
	// Baseline the "last valid" selection from the incoming props when the panel opens (assumed valid).
	const baselineValid = useCallback(() => {
		lastValidRef.current = committedIds;
	}, [committedIds]);
	const committedKey = committedIds.join(" ");

	// M4: keep the apply-mode buffer in sync when the committed props change while the panel is OPEN
	// (e.g. an async load resolves underneath an open sheet). Not run in live mode (no buffer) or while
	// closed (openPanel's resetBuffer handles the fresh open). Keyed on committedKey so it fires only
	// on real content changes, not array identity.
	// biome-ignore lint/correctness/useExhaustiveDependencies: committedIds is captured by committedKey; adding the array would re-run on every render.
	useEffect(() => {
		if (commit === "apply" && open) setBufferIds(committedIds);
	}, [commit, open, committedKey]);

	const effectiveIds = useMemo(() => new Set(commit === "apply" && multiple ? bufferIds : committedIds), [commit, multiple, bufferIds, committedIds]);

	const idsToValues = useCallback((ids: string[]) => ids.map((id) => valueById.get(id)).filter((v): v is Extracted<T> => v !== undefined), [valueById]);

	// Commit a next id set (multi) either straight to the caller (live) or into the buffer (apply).
	// `maximalSelection` still refuses going ABOVE the cap (a deselect only lowers the count, so it's
	// never blocked). `minimalSelection` is NOT enforced here — the user may freely deselect down to 0
	// while open; falling below the min is corrected on close (restore last-valid + snackbar). We record
	// the last selection that met the min so we can revert to it.
	const commitMulti = useCallback(
		(nextIds: string[]) => {
			if (maximalSelection !== undefined && nextIds.length > maximalSelection) {
				onMaxExceeded?.();
				return;
			}
			if (minimalSelection === undefined || nextIds.length >= minimalSelection) lastValidRef.current = nextIds;
			if (commit === "apply") {
				setBufferIds(nextIds);
				return;
			}
			onChangeMultiple?.(idsToValues(nextIds));
		},
		[maximalSelection, minimalSelection, commit, onChangeMultiple, idsToValues, onMaxExceeded],
	);

	// True when the current (effective) selection is below the required minimum — checked on close.
	// Only fires when there is a prior valid (>= min) selection to restore to: a field that legitimately
	// opened already below the min (e.g. a nurse with no jobs yet) is left alone rather than nagged.
	const minViolated = !!multiple && minimalSelection !== undefined && effectiveIds.size < minimalSelection && lastValidRef.current.length >= minimalSelection;
	const restoreLastValid = useCallback(() => onChangeMultiple?.(idsToValues(lastValidRef.current)), [onChangeMultiple, idsToValues]);

	const toggleId = useCallback(
		(id: string) => {
			if (!multiple) {
				onChange?.(valueById.get(id));
				return;
			}
			if (multiAsOne) {
				commitMulti([id]);
				return;
			}
			const next = new Set(effectiveIds);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			// Preserve item order for stable value arrays.
			commitMulti(selectableIds.filter((sid) => next.has(sid)));
		},
		[multiple, multiAsOne, onChange, valueById, effectiveIds, selectableIds, commitMulti],
	);

	// Tri-state Select-all, scoped to the passed (visible) ids: if all visible are already selected → deselect
	// just those; otherwise add the missing ones. Off-screen selections are never touched.
	const toggleVisible = useCallback(
		(visibleIds: string[]) => {
			if (visibleIds.length === 0) return;
			const allVisibleSelected = visibleIds.every((id) => effectiveIds.has(id));
			const next = new Set(effectiveIds);
			if (allVisibleSelected) for (const id of visibleIds) next.delete(id);
			else for (const id of visibleIds) next.add(id);
			commitMulti(selectableIds.filter((sid) => next.has(sid)));
		},
		[effectiveIds, selectableIds, commitMulti],
	);

	const selectAll = useCallback(() => commitMulti([...selectableIds]), [commitMulti, selectableIds]);
	const clearAll = useCallback(() => commitMulti([]), [commitMulti]);

	// `groupIds` is the exact set the panel wants toggled — already narrowed to the group's visible, enabled,
	// selectable children (headers/disabled/filtered-out rows are excluded before we get here). Tri-state like
	// `toggleVisible`: if all are already selected, deselect just those; otherwise add the missing ones.
	const toggleGroup = useCallback(
		(groupIds: string[]) => {
			if (groupIds.length === 0) return;
			const allSelected = groupIds.every((id) => effectiveIds.has(id));
			const next = new Set(effectiveIds);
			if (allSelected) for (const id of groupIds) next.delete(id);
			else for (const id of groupIds) next.add(id);
			commitMulti(selectableIds.filter((sid) => next.has(sid)));
		},
		[effectiveIds, selectableIds, commitMulti],
	);

	const applyBuffer = useCallback(
		(close: () => void) => {
			onChangeMultiple?.(idsToValues(bufferIds));
			close();
		},
		[onChangeMultiple, idsToValues, bufferIds],
	);

	const isAllSelected = useMemo(() => selectableIds.length > 0 && selectableIds.every((id) => effectiveIds.has(id)), [selectableIds, effectiveIds]);

	return {
		idOf,
		normalizedItems,
		selectableItems,
		selectableIds,
		valueById,
		duplicateSelectionKey,
		effectiveIds,
		isAllSelected,
		toggleId,
		toggleVisible,
		toggleGroup,
		selectAll,
		clearAll,
		resetBuffer,
		baselineValid,
		minViolated,
		restoreLastValid,
		applyBuffer,
	};
};

export default useDropdownState;
